import type { FlowOperatorMetadata, CampaignType, OperatorKind } from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import type { FlowStructureDto, FlowStepDto } from '@/types/flow-structure';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { i18n } from '@/i18n';
import { isOnPeriodicChain } from '@/composables/segmentations/segmentationValidation';
import { predefinedEvents } from '@/composables/events/constants';
import { getEffectiveOperatorId, getSelectableEventBoxIds, TRIGGER_INTERNAL_EVENT_ID } from '@/composables/flows/compatibility/effective-id';

// ===== Triggers =====
import PeriodicLaunching from './triggers/PeriodicLaunching';
import AbandonCart from './triggers/AbandonCart';
import StatusCommand from './triggers/StatusCommand';
import RegisterOnSite from './triggers/RegisterOnSite';
import SubscribeNewsletter from './triggers/SubscribeNewsletter';
import QuitSite from './triggers/QuitSite';
import ScrollPage from './triggers/ScrollPage';
import CustomJS from './triggers/CustomJS';
import SitePassTime from './triggers/SitePassTime';
import VisitPage from './triggers/VisitPage';
import TriggerInternalEvent from './triggers/TriggerInternalEvent';
import AudienceSetup from './triggers/AudienceSetup';

// ===== Filters =====
import Customer from './filters/Customer';
import ContactSegmentation from './filters/ContactSegmentation';
import OpenEmail from './filters/OpenEmail';
import ClickLink from './filters/ClickLink';
import FilledOutForm from './filters/FilledOutForm';
import CurrentCart from './filters/CurrentCart';
import CurrentOrder from './filters/CurrentOrder';
import PurchaseHistory from './filters/PurchaseHistory';
import NavigationHistory from './filters/NavigationHistory';

// ===== Actions =====
import SendEmail from './actions/SendEmail';
import SendSms from './actions/SendSms';
import SendPushNotification from './actions/SendPushNotification';
import SendWhatsapp from './actions/SendWhatsapp';
import SendRcs from './actions/SendRcs';
import DisplayPopup from './actions/DisplayPopup';
import DisplayFreeModel from './actions/DisplayFreeModel';
import Wait from './actions/Wait';
import ABTest from './actions/ABTest';
import DelaisMax from './actions/DelaisMax';
import List from './actions/List';
import Tag from './actions/Tag';

// ===== Maps for quick access =====

/** Map by ID (e.g., 'boxperiodiclaunching') */
const operatorsById = new Map<string, FlowOperatorMetadata>([
  // Triggers
  [PeriodicLaunching.id, PeriodicLaunching],
  [AbandonCart.id, AbandonCart],
  [StatusCommand.id, StatusCommand],
  [RegisterOnSite.id, RegisterOnSite],
  [SubscribeNewsletter.id, SubscribeNewsletter],
  [QuitSite.id, QuitSite],
  [ScrollPage.id, ScrollPage],
  [CustomJS.id, CustomJS],
  [SitePassTime.id, SitePassTime],
  [VisitPage.id, VisitPage],
  [TriggerInternalEvent.id, TriggerInternalEvent],
  [AudienceSetup.id, AudienceSetup],
  // Legacy alias: boxchoosebulklist is now handled by AudienceSetup
  ['boxchoosebulklist', AudienceSetup],

  // Filters
  [Customer.id, Customer],
  [ContactSegmentation.id, ContactSegmentation],
  [OpenEmail.id, OpenEmail],
  [ClickLink.id, ClickLink],
  [FilledOutForm.id, FilledOutForm],
  [CurrentCart.id, CurrentCart],
  [CurrentOrder.id, CurrentOrder],
  [PurchaseHistory.id, PurchaseHistory],
  [NavigationHistory.id, NavigationHistory],

  // Actions
  [SendEmail.id, SendEmail],
  [SendSms.id, SendSms],
  [SendPushNotification.id, SendPushNotification],
  [SendWhatsapp.id, SendWhatsapp],
  [SendRcs.id, SendRcs],
  [DisplayPopup.id, DisplayPopup],
  [DisplayFreeModel.id, DisplayFreeModel],
  [Wait.id, Wait],
  [ABTest.id, ABTest],
  [DelaisMax.id, DelaisMax],
  [List.id, List],
  [Tag.id, Tag],
]);


// ===== Access functions =====

/**
 * Get an operator by its ID
 * Equivalent to getComponentMetadataById
 * @example getOperatorById('boxperiodiclaunching')
 */
export function getOperatorById(id: string): FlowOperatorMetadata | null {
  return operatorsById.get(id) ?? null;
}

/**
 * List all operators (deduplicated, as some operators have legacy aliases)
 */
export function getAllOperators(): FlowOperatorMetadata[] {
  // Use Set to deduplicate since some operators have legacy aliases pointing to the same object
  return Array.from(new Set(operatorsById.values()));
}

/**
 * List operators by kind (trigger, action, filter)
 */
export function getOperatorsByKind(kind: OperatorKind): FlowOperatorMetadata[] {
  return getAllOperators().filter(op => op.kind === kind);
}

/**
 * List operators available for a campaign type
 */
export function getAvailableOperators(campaignType: CampaignType): FlowOperatorMetadata[] {
  return getAllOperators().filter(op => op.availableIn.includes(campaignType));
}

/**
 * Context for filtering compatible operators
 */
export interface OperatorInsertContext {
  /** Campaign type (automation or bulk) */
  campaignType: CampaignType;
  /** Whether inserting at root position (no parent) */
  isRoot: boolean;
  /** Insert position relative to the clicked node */
  insertPosition: 'before' | 'after' | 'replace' | 'closure';
  /** Parent operator ID (when inserting after a node) */
  parentOperatorId?: string;
  /** Parent operator data (for checking subcomponent incompatibilities) */
  parentOperatorData?: Record<string, any>;
  /** Child operator ID (when inserting before a node) */
  childOperatorId?: string;
  /** Output index when inserting after a node with multiple outputs */
  outputIndex?: number;
  /** Pre-selected operator (to skip selection and go directly to form) */
  preSelectedOperator?: FlowOperatorMetadata;
  /** All ancestor operator types (for dependency checking) */
  allAncestorTypes?: string[];
  /** All ancestor step IDs (for linked operator selection) */
  allAncestorStepIds?: string[];
  /** Outputs to close together (for closure mode) */
  closureOutputs?: Array<{ stepId: string; output: number }>;
  /** Direct parent operator types (for closure mode incompatibility check) */
  directParentTypes?: string[];
  /** Step ID of the parent (distinct from parentOperatorId which holds the type) */
  parentStepId?: string;
  /** Full flow structure — enables graph traversal for context-aware filtering */
  flow?: FlowStructureDto;
}

/**
 * Event `code_name`s that are incompatible as a child of the given parent.
 * Used by the internal-event form to hide non-selectable events (clean UX)
 * instead of letting the user pick one the validator would then flag.
 */
export function getIncompatibleEventValues(context: {
  parentOperatorId?: string;
  parentOperatorData?: Record<string, any>;
  campaignType: CampaignType;
} | null | undefined): string[] {
  if (!context?.parentOperatorId) {
    return [];
  }
  const parentEffectiveId = getEffectiveOperatorId({
    id: context.parentOperatorId,
    event: context.parentOperatorData?.event,
  });
  const incompatibleIds = getOperatorById(parentEffectiveId)?.incompatibilities?.[context.campaignType]?.nextOperators ?? [];
  if (incompatibleIds.length === 0) {
    return [];
  }
  return Object.values(predefinedEvents)
    .filter((event) => event.display && incompatibleIds.includes(event.subcomponent))
    .map((event) => event.code_name);
}

/**
 * Get operators compatible with a given insertion context
 * Filters based on:
 * - Position constraints (root = triggers only)
 * - Parent incompatibilities (nextOperators)
 * - Child compatibility (when inserting before or replacing)
 */
export function getCompatibleOperators(context: OperatorInsertContext): FlowOperatorMetadata[] {
  return getAllOperators().filter(op => {
    // Rule 0: Hidden operators are not shown in selection
    if (op.hidden) {
      return false;
    }

    // Rule 1: Must be available for this campaign type
    if (!op.availableIn.includes(context.campaignType)) {
      return false;
    }

    // Rule 2: Root position = triggers only
    // For non-root positions, triggers are filtered by incompatibilities (Rule 3)
    // In BULK campaigns, triggers should not appear in non-root positions
    if (context.isRoot && op.kind !== 'trigger') {
      return false;
    }
    if (!context.isRoot && op.kind === 'trigger' && context.campaignType === TypeCampaignEnum.BULK) {
      return false;
    }

    // Rule 3: Check parent incompatibilities (when inserting after, before, or replacing)
    // The new operator must be compatible as a child of the parent
    // This also handles triggers in non-root positions - they are filtered by parent incompatibilities
    if (context.parentOperatorId) {
      // Resolve the parent to its effective id so an internal-event trigger uses
      // the incompatibilities of its event's box (e.g. event `visit_page` →
      // `boxvisitpage`), not the generic wrapper's coarse list.
      const parentEffectiveId = getEffectiveOperatorId({
        id: context.parentOperatorId,
        event: context.parentOperatorData?.event,
      });
      const parent = getOperatorById(parentEffectiveId);
      const incompatibleIds = parent?.incompatibilities?.[context.campaignType]?.nextOperators ?? [];

      if (op.id === TRIGGER_INTERNAL_EVENT_ID) {
        // The concrete event isn't chosen yet at picker time, so testing the
        // wrapper id against the parent's list would blanket-block every event.
        // Offer the internal-event when at least one of its events resolves to a
        // box the parent allows; the event-aware validator flags a bad choice.
        const anyEventCompatible = getSelectableEventBoxIds().some(
          (eventBoxId) => !incompatibleIds.includes(eventBoxId),
        );
        if (!anyEventCompatible) {
          return false;
        }
      } else if (incompatibleIds.includes(op.id)) {
        return false;
      }
    }

    // Rule 3b: For closure, check incompatibilities with DIRECT parent operators only
    // (same as old system which uses directParentsId, not allParentsId for incompatibilities)
    if (context.insertPosition === 'closure' && context.directParentTypes) {
      for (const parentType of context.directParentTypes) {
        const parentOp = getOperatorById(parentType);
        const incompatibleIds = parentOp?.incompatibilities?.[context.campaignType]?.nextOperators ?? [];
        if (incompatibleIds.includes(op.id)) {
          return false;
        }
      }
    }

    // Rule 3c: On the NO output (2) of a boxcontactseg that already belongs to a
    // periodic chain, the only allowed child is another boxcontactseg with a date
    // condition. Other operator types would forward an unbounded contact volume.
    if (
      context.insertPosition === 'after'
      && context.outputIndex === 2
      && context.parentOperatorId === 'boxcontactseg'
      && context.parentStepId
      && isOnPeriodicChain(context.parentStepId, context.flow)
      && op.id !== 'boxcontactseg'
    ) {
      return false;
    }

    // Rule 3d: Same constraint for branch closure — if any selected output is a NO
    // branch (2) of a boxcontactseg on a periodic chain, the joining box must be
    // a boxcontactseg too, otherwise the merge would propagate an unbounded volume.
    if (
      context.insertPosition === 'closure'
      && context.closureOutputs
      && op.id !== 'boxcontactseg'
    ) {
      const closingOnPeriodicNoBranch = context.closureOutputs.some((selected) => {
        if (selected.output !== 2) return false;
        const sourceStep = context.flow?.steps.find((s) => s.id === selected.stepId);
        if (sourceStep?.type !== 'boxcontactseg') return false;
        return isOnPeriodicChain(selected.stepId, context.flow);
      });
      if (closingOnPeriodicNoBranch) {
        return false;
      }
    }

    // Rule 4: Check dependencies (operator only appears if required parent operators exist)
    if (op.dependencies) {
      const deps = op.dependencies[context.campaignType];
      if (deps?.operators) {
        const anyParents = deps.operators.any_parents ?? [];
        if (anyParents.length > 0) {
          // Check if any of the required parent types exist in ancestors
          const ancestorTypes = context.allAncestorTypes ?? [];
          const hasRequiredParent = anyParents.some(dep => {
            const depId = typeof dep === 'string' ? dep : dep.boxId;
            return ancestorTypes.includes(depId);
          });
          if (!hasRequiredParent) {
            return false;
          }
        }
      }
    }

    return true;
  });
}

/**
 * Group operators by kind for display in the panel
 */
export function groupOperatorsByKind(operators: FlowOperatorMetadata[]): {
  triggers: FlowOperatorMetadata[];
  filters: FlowOperatorMetadata[];
  actions: FlowOperatorMetadata[];
} {
  return {
    triggers: operators.filter(op => op.kind === 'trigger'),
    filters: operators.filter(op => op.kind === 'filter'),
    actions: operators.filter(op => op.kind === 'action'),
  };
}

/**
 * List incompatible operators after a given operator
 */
export function getIncompatibleOperators(
  operatorId: string,
  campaignType: CampaignType,
): string[] {
  const operator = operatorsById.get(operatorId);
  return operator?.incompatibilities?.[campaignType]?.nextOperators ?? [];
}

// ===== Deletion Check Helpers =====

/**
 * Check if a step is at the root of the flow (has no parents)
 */
export function isRootStep(flow: FlowStructureDto, stepId: string): boolean {
  const step = flow.steps.find(s => s.id === stepId);
  return step ? step.parents.length === 0 : false;
}

/**
 * Get children of a step
 */
export function getStepChildren(flow: FlowStructureDto, stepId: string): FlowStepDto[] {
  const step = flow.steps.find(s => s.id === stepId);
  if (!step) return [];
  return step.children
    .map(child => flow.steps.find(s => s.id === child.id))
    .filter((s): s is FlowStepDto => s !== undefined);
}

/**
 * Get all descendant step IDs recursively
 */
function getAllDescendantIds(flow: FlowStructureDto, stepId: string): string[] {
  const descendants: string[] = [];
  const visited = new Set<string>();

  const traverse = (currentId: string) => {
    if (visited.has(currentId)) return;
    visited.add(currentId);

    const step = flow.steps.find(s => s.id === currentId);
    if (!step) return;

    for (const child of step.children) {
      descendants.push(child.id);
      traverse(child.id);
    }
  };

  traverse(stepId);
  return descendants;
}

/**
 * Operator types that can be linked to a parent (e.g., openemail linked to sendemail)
 */
const LINKABLE_OPERATOR_TYPES = ['openemail', 'filledoutform', 'clicklink'];

/**
 * Check if any descendant operators are linked to the given step
 * Used for Send* operators to prevent deletion if linked filters exist
 */
export function getLinkedOperators(flow: FlowStructureDto, stepId: string): string[] {
  const linkedOperators: string[] = [];
  const descendantIds = getAllDescendantIds(flow, stepId);

  for (const descendantId of descendantIds) {
    const step = flow.steps.find(s => s.id === descendantId);
    if (!step) continue;

    // Use step.type directly as the operator ID
    const operatorType = step.type;

    if (LINKABLE_OPERATOR_TYPES.includes(operatorType)) {
      // Check if this operator is linked to the step we want to delete
      const params = parseOperatorParameters(step.parameters);
      let linkedBoxArray: string[] = [];
      const linkedBox = params.linked_box;

      if (linkedBox) {
        if (typeof linkedBox === 'string') {
          try {
            linkedBoxArray = JSON.parse(linkedBox);
          } catch {
            linkedBoxArray = [linkedBox];
          }
        } else if (Array.isArray(linkedBox)) {
          linkedBoxArray = linkedBox;
        }

        if (linkedBoxArray.includes(stepId)) {
          const customTitle = params.custom_title as string || operatorType;
          linkedOperators.push(customTitle);
        }
      }
    }
  }

  return linkedOperators;
}

/**
 * Check if a trigger can be deleted
 * Triggers at root cannot be deleted if they have non-trigger children
 */
export async function canDeleteTrigger(flow: FlowStructureDto, stepId: string): Promise<boolean | string> {
  const translation = await i18n;

  if (isRootStep(flow, stepId)) {
    const children = getStepChildren(flow, stepId);
    for (const child of children) {
      // Use step.type directly as the operator ID
      const childOperator = getOperatorById(child.type);
      if (childOperator && childOperator.kind !== 'trigger') {
        return translation.global.t('automatedScenarios.cant_remove_operator');
      }
    }
  }

  return true;
}

/**
 * Check if a sending action (email, sms, etc.) can be deleted
 * Cannot be deleted if linked operators exist
 */
export async function canDeleteSendingAction(flow: FlowStructureDto, stepId: string): Promise<boolean | string> {
  const translation = await i18n;
  const linkedOperators = getLinkedOperators(flow, stepId);

  if (linkedOperators.length > 0) {
    const listBoxes = linkedOperators.join(', ');
    return translation.global.t('automatedScenarios.box_dialog.delete_operator.warning_linked_box', { boxes: listBoxes });
  }

  return true;
}

// ===== Persistence Calculation =====

type PersistenceList = Record<string, string[]>;

/**
 * Compute persistence for a single step based on its metadata and active segments
 * Uses operator.id (e.g., 'boxfiltre_3') as key for translation lookup
 */
function computeStepPersistence(
  operator: FlowOperatorMetadata | undefined,
  stepData: Record<string, any>,
  inheritedPersistence: PersistenceList,
  stepId: string
): PersistenceList {
  const result: PersistenceList = { ...inheritedPersistence };

  // A filter with its "Persistent filter" switch off is not persistent, whatever
  // customPersistance an earlier save left behind: the engine only reads
  // is_persistent. Unticking the switch used to leave that value in place, so
  // saved filters kept the "P" badge until the switch was toggled again.
  const persistenceSwitchedOff = 'is_persistent' in stepData && String(stepData.is_persistent) !== '1';

  // Add custom persistence from the operator's data (e.g., TriggerInternalEvent with persistant events)
  if (!persistenceSwitchedOff && stepData.customPersistance && typeof stepData.customPersistance === 'object') {
    // Replace {CURRENT_OPERATOR_ID} placeholder with actual step ID (same as old system)
    let customPersistance = stepData.customPersistance;
    if (/{CURRENT_OPERATOR_ID}/.test(JSON.stringify(customPersistance))) {
      customPersistance = JSON.parse(
        JSON.stringify(customPersistance).replace(/\{CURRENT_OPERATOR_ID\}/g, stepId)
      );
    }

    for (const [key, values] of Object.entries(customPersistance)) {
      if (Array.isArray(values)) {
        if (!result[key]) {
          result[key] = [];
        }
        for (const value of values) {
          if (!result[key].includes(value as string)) {
            result[key].push(value as string);
          }
        }
      }
    }
  }

  if (!operator?.persistent) {
    return result;
  }

  const inclusion = stepData.inclusion || {};
  const exclusion = stepData.exclusion || {};
  const operatorTypeId = operator.id; // Use operator type ID for translation

  for (const config of operator.persistent) {
    if (typeof config === 'string') {
      // Simple persistence: entire operator is persistent
      result[operatorTypeId] = [operatorTypeId];
    } else {
      // Complex persistence: only specific segments are persistent
      const { segmentId } = config;

      if (Object.keys(inclusion).includes(segmentId) || Object.keys(exclusion).includes(segmentId)) {
        if (!result[operatorTypeId]) {
          result[operatorTypeId] = [];
        }
        if (!result[operatorTypeId].includes(segmentId)) {
          result[operatorTypeId].push(segmentId);
        }
      }
    }
  }

  return result;
}

/**
 * Calculate the persistence list for a step by traversing parents and computing persistence
 * Returns a map of operator IDs to their persistent segment IDs
 */
export function calculatePersistenceForStep(
  flow: FlowStructureDto,
  stepId: string,
  visited: Set<string> = new Set()
): PersistenceList {
  // Prevent infinite recursion
  if (visited.has(stepId)) return {};
  visited.add(stepId);

  const step = flow.steps.find(s => s.id === stepId);
  if (!step) return {};

  // Get all parent persistence lists
  const parentIds = getParentIds(flow, stepId);
  let inheritedPersistence: PersistenceList = {};

  // Recursively get persistence from parents
  for (const parentId of parentIds) {
    const parentPersistence = calculatePersistenceForStep(flow, parentId, visited);
    // Merge parent persistence
    for (const [key, values] of Object.entries(parentPersistence)) {
      if (!inheritedPersistence[key]) {
        inheritedPersistence[key] = [];
      }
      for (const value of values) {
        if (!inheritedPersistence[key].includes(value)) {
          inheritedPersistence[key].push(value);
        }
      }
    }
  }

  // Get operator metadata
  const operator = getOperatorById(step.type) ?? undefined;
  const stepData = parseOperatorParameters(step.parameters || {});

  // Compute this step's persistence
  return computeStepPersistence(operator, stepData, inheritedPersistence, stepId);
}

/**
 * Get parent IDs for a step
 */
function getParentIds(flow: FlowStructureDto, stepId: string): string[] {
  const step = flow.steps.find(s => s.id === stepId);
  if (!step) return [];

  const parentIds: string[] = [];
  if (step.primaryParent?.id) {
    parentIds.push(step.primaryParent.id);
  }
  // Add secondary parents if any
  if (step.parents) {
    for (const parent of step.parents) {
      if (parent.id && !parentIds.includes(parent.id)) {
        parentIds.push(parent.id);
      }
    }
  }
  return parentIds;
}

// ===== Sending action IDs =====
const SENDING_ACTION_IDS = [
  'boxsendemail',
  'boxsendsms',
  'boxsendpushnotification',
  'boxsendwhatsapp',
  'boxsendrcs',
];

/**
 * Check if a flow has at least one sending action step
 */
export function flowHasSendingAction(flow: FlowStructureDto): boolean {
  if (!flow || !flow.steps || flow.steps.length === 0) {
    return false;
  }
  return flow.steps.some(step => SENDING_ACTION_IDS.includes(step.type));
}

/**
 * Check if a flow has at least one operator
 */
export function flowHasOperators(flow: FlowStructureDto): boolean {
  return flow && flow.steps && flow.steps.length > 0;
}

// ===== Export all operators for direct access =====

export {
  PeriodicLaunching,
  AbandonCart,
  StatusCommand,
  RegisterOnSite,
  SubscribeNewsletter,
  QuitSite,
  ScrollPage,
  CustomJS,
  SitePassTime,
  VisitPage,
  TriggerInternalEvent,
  AudienceSetup,
  Customer,
  SendEmail,
  SendSms,
  SendPushNotification,
  SendWhatsapp,
  SendRcs,
  DisplayPopup,
  DisplayFreeModel,
  Wait,
};
